import dayjs, { type Dayjs } from 'dayjs';

import type { MemberRole } from './staff-api';
import { bucketOf, isLate } from './work-queue';
import type { WorkItem, WorkKind, WorkSkill } from './workflow-api';

/**
 * Faceted filtering, preset views and the URL codec for the work queue
 * (phase 177).
 *
 * Separate from `work-queue.ts` on purpose: that module answers "which bucket
 * is this row in and what does it say", this one answers "which rows is the
 * user looking at". Phase 174 could keep both in one file because there were
 * three facets; six kinds and nine facets is a different job with a different
 * test file.
 *
 * Every function here is pure and takes `now`/`userId` explicitly rather than
 * closing over them, so the tests can pin the clock and the viewer.
 */

/* ----------------------------------------------------------------- kinds -- */

export const WORK_KINDS: readonly WorkKind[] = [
  'follow_up',
  'checklist',
  'card',
  'review',
  'grading',
  'assignment',
  'message',
  'task',
];

export const WORK_SKILLS: readonly WorkSkill[] = ['listening', 'reading', 'writing', 'speaking'];

export const SKILL_LABELS: Readonly<Record<WorkSkill, string>> = {
  listening: 'Listening',
  reading: 'Reading',
  writing: 'Writing',
  speaking: 'Speaking',
};

/* --------------------------------------------------------------- filters -- */

/**
 * A state is a cross-cutting question ("what is stuck?") that no single column
 * answers. Kind and assignee are separate facets because they compose with it:
 * "unassigned grading" is a real question and a single flat status list could
 * not ask it.
 */
export type StateFilter =
  'any' | 'now' | 'overdue' | 'today' | 'week' | 'unassigned' | 'waiting_student' | 'blocked';

export const STATE_OPTIONS: ReadonlyArray<{ key: StateFilter; label: string }> = [
  { key: 'any', label: 'Any state' },
  { key: 'now', label: 'Overdue or due today' },
  { key: 'overdue', label: 'Overdue or late' },
  { key: 'today', label: 'Due today' },
  { key: 'week', label: 'Due this week' },
  { key: 'unassigned', label: 'Nobody has it' },
  { key: 'waiting_student', label: 'Waiting on the student' },
  { key: 'blocked', label: 'Blocked' },
];

/** `'any'` | `'me'` | `'unassigned'` | a staff user id. */
export type AssigneeFilter = string;

export type SortBy = 'due' | 'due_desc' | 'student' | 'kind';

export const SORT_OPTIONS: ReadonlyArray<{ key: SortBy; label: string }> = [
  { key: 'due', label: 'Oldest first' },
  { key: 'due_desc', label: 'Newest first' },
  { key: 'student', label: 'Student A–Z' },
  { key: 'kind', label: 'Kind' },
];

export interface WorkFilters {
  /** Empty ⇒ every kind. Same idiom as `SkillFilter`: selecting none is not
   *  "show nothing", it is "no opinion". */
  kinds: ReadonlySet<WorkKind>;
  state: StateFilter;
  assignee: AssigneeFilter;
  /** Null = any board. Kinds with no board pass only when this is null. */
  boardId: string | null;
  batchId: string | null;
  /** Empty ⇒ every skill. Only `grading` rows carry skills at all. */
  skills: ReadonlySet<WorkSkill>;
  /** Inclusive `YYYY-MM-DD` bounds on `due_at`. */
  from: string | null;
  to: string | null;
  search: string;
  /** Server-side: adds the caller's undated checklist items. */
  undated: boolean;
  sort: SortBy;
}

export const NO_FILTERS: WorkFilters = {
  kinds: new Set(),
  state: 'any',
  assignee: 'any',
  boardId: null,
  batchId: null,
  skills: new Set(),
  from: null,
  to: null,
  search: '',
  undated: false,
  sort: 'due',
};

export interface FilterContext {
  now: Dayjs;
  userId: string | null;
}

function matchesAssignee(row: WorkItem, assignee: AssigneeFilter, userId: string | null): boolean {
  if (assignee === 'any') return true;
  if (assignee === 'unassigned') return row.is_unassigned;
  if (assignee === 'me') return userId !== null && row.assignee_id === userId;
  return row.assignee_id === assignee;
}

function matchesState(row: WorkItem, state: StateFilter, now: Dayjs): boolean {
  switch (state) {
    case 'any':
      return true;
    // 'now' buckets by the raw date, so a file that arrived this morning counts
    // as today's work. 'overdue' goes through isLate, which gives the aged
    // kinds their 48-hour grace — the two questions are genuinely different.
    case 'now':
      return ['overdue', 'today'].includes(bucketOf(row.due_at, now));
    case 'overdue':
      return isLate(row, now);
    case 'today':
      return bucketOf(row.due_at, now) === 'today';
    case 'week':
      return ['overdue', 'today', 'week'].includes(bucketOf(row.due_at, now));
    case 'unassigned':
      return row.is_unassigned;
    case 'waiting_student':
      return row.waiting_on_student;
    case 'blocked':
      return row.blocked_reason !== null;
  }
}

function matchesDateRange(row: WorkItem, from: string | null, to: string | null): boolean {
  if (from === null && to === null) return true;
  // An undated row cannot satisfy a range. Saying otherwise would make "due
  // before Friday" quietly include everything with no date at all.
  if (row.due_at === null) return false;
  // Both bounds are the VIEWER's calendar day, inclusive at each end.
  const due = dayjs(row.due_at);
  const afterStart = from === null || !due.isBefore(dayjs(from).startOf('day'));
  const beforeEnd = to === null || !due.isAfter(dayjs(to).endOf('day'));
  return afterStart && beforeEnd;
}

/**
 * Everything except the kind facet — the half that `countsForKinds` needs to
 * apply so a chip's number is the number you get by clicking it.
 */
function matchesExceptKind(row: WorkItem, filters: WorkFilters, ctx: FilterContext): boolean {
  if (!matchesAssignee(row, filters.assignee, ctx.userId)) return false;
  if (!matchesState(row, filters.state, ctx.now)) return false;
  if (filters.boardId !== null && row.board_id !== filters.boardId) return false;
  if (filters.batchId !== null && row.batch_id !== filters.batchId) return false;
  if (filters.skills.size > 0 && !row.skills.some((skill) => filters.skills.has(skill))) {
    return false;
  }
  if (!matchesDateRange(row, filters.from, filters.to)) return false;

  const needle = filters.search.trim().toLowerCase();
  if (needle !== '') {
    const haystack =
      `${row.student_name} ${row.title} ${row.subtitle ?? ''} ${row.batch_name ?? ''}`.toLowerCase();
    if (!haystack.includes(needle)) return false;
  }
  return true;
}

export function filterWork(
  rows: readonly WorkItem[],
  filters: WorkFilters,
  ctx: FilterContext,
): WorkItem[] {
  return rows.filter((row) => {
    if (filters.kinds.size > 0 && !filters.kinds.has(row.kind)) return false;
    return matchesExceptKind(row, filters, ctx);
  });
}

// A message sits at the top with grading and review: it is the only kind where
// a person is waiting on a human reply, and it goes stale fastest.
const KIND_ORDER: Readonly<Record<WorkKind, number>> = {
  message: 0,
  grading: 1,
  review: 2,
  follow_up: 3,
  checklist: 4,
  task: 5,
  card: 6,
  assignment: 7,
};

/** Sorting is stable and total: ties fall back to the server's due order, so a
 *  re-render never reshuffles rows the user was about to click. */
export function sortWork(rows: readonly WorkItem[], by: SortBy): WorkItem[] {
  const out = [...rows];
  if (by === 'due') return out;
  out.sort((a, b) => {
    if (by === 'student') {
      const byName = a.student_name.localeCompare(b.student_name);
      if (byName !== 0) return byName;
    } else if (by === 'kind') {
      const byKind = KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
      if (byKind !== 0) return byKind;
    }
    const da = a.due_at === null ? null : dayjs(a.due_at).valueOf();
    const db = b.due_at === null ? null : dayjs(b.due_at).valueOf();
    if (da === null && db === null) return 0;
    if (da === null) return 1;
    if (db === null) return -1;
    return by === 'due_desc' ? db - da : da - db;
  });
  return out;
}

/* ---------------------------------------------------------------- counts -- */

export type KindCounts = Record<WorkKind | 'all', number>;

/**
 * Honest chip counts. Every facet EXCEPT `kinds` is applied, so each number
 * answers "how many rows do I get if I click this chip" — not "how many exist
 * somewhere in the unfiltered list", which is what a naive tally reports and
 * which sends people clicking a chip labelled 12 to find four rows.
 */
export function countsForKinds(
  rows: readonly WorkItem[],
  filters: WorkFilters,
  ctx: FilterContext,
): KindCounts {
  const counts: KindCounts = {
    all: 0,
    follow_up: 0,
    checklist: 0,
    card: 0,
    review: 0,
    grading: 0,
    assignment: 0,
    message: 0,
    task: 0,
  };
  for (const row of rows) {
    if (!matchesExceptKind(row, filters, ctx)) continue;
    counts.all += 1;
    counts[row.kind] += 1;
  }
  return counts;
}

/* --------------------------------------------------------------- lookups -- */

export interface Lookup {
  id: string;
  name: string;
}

function distinctBy(
  rows: readonly WorkItem[],
  id: (row: WorkItem) => string | null,
  name: (row: WorkItem) => string | null,
  fallback: string,
): Lookup[] {
  const seen = new Map<string, string>();
  for (const row of rows) {
    const key = id(row);
    if (key !== null && !seen.has(key)) seen.set(key, name(row) ?? fallback);
  }
  return [...seen.entries()]
    .map(([id_, name_]) => ({ id: id_, name: name_ }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The boards represented in the list, for the board filter. */
export function boardsIn(rows: readonly WorkItem[]): Lookup[] {
  return distinctBy(
    rows,
    (row) => row.board_id,
    (row) => row.board_name,
    'Board',
  );
}

/** The batches represented in the list, for the batch filter. */
export function batchesIn(rows: readonly WorkItem[]): Lookup[] {
  return distinctBy(
    rows,
    (row) => row.batch_id,
    (row) => row.batch_name,
    'Batch',
  );
}

/* --------------------------------------------------------------- presets -- */

export interface WorkPreset {
  key: string;
  label: string;
  /** What the preset sets on top of `NO_FILTERS`. */
  filters: Partial<WorkFilters>;
}

/**
 * Preset views. These are code, not rows in a table: a preset here is a
 * starting point that the URL then makes shareable, and a link IS the saved
 * view. Boards have server-side saved views because a board's filters are
 * per-board data; a queue's are not.
 */
export const PRESETS: readonly WorkPreset[] = [
  { key: 'today', label: 'Today', filters: { state: 'now', assignee: 'me' } },
  {
    key: 'my-teaching',
    label: 'My teaching',
    filters: { kinds: new Set<WorkKind>(['grading', 'assignment']), assignee: 'me' },
  },
  { key: 'to-grade', label: 'To grade', filters: { kinds: new Set<WorkKind>(['grading']) } },
  {
    key: 'assignments',
    label: 'Not handed in',
    filters: { kinds: new Set<WorkKind>(['assignment']) },
  },
  { key: 'to-review', label: 'Files to review', filters: { kinds: new Set<WorkKind>(['review']) } },
  // Phase 179b. A preset rather than only a chip: an unanswered student is the
  // one kind of work where somebody is actively waiting on a person, and the
  // inbox stops being read the moment reaching it costs two clicks.
  { key: 'to-answer', label: 'To answer', filters: { kinds: new Set<WorkKind>(['message']) } },
  { key: 'unassigned', label: 'Nobody has it', filters: { assignee: 'unassigned' } },
  { key: 'all', label: 'Everything', filters: {} },
];

export const DEFAULT_PRESET = 'today';

/**
 * Where each role lands on a bare `/institute/work`.
 *
 * Roles set the DEFAULT and nothing else — every preset and every facet stays
 * available to everyone, which is the same call phase 152 made when it opened
 * board configuration to teachers and examiners: hiding a control a colleague
 * mentions on the phone makes the feature look missing rather than forbidden.
 */
export const ROLE_PRESET: Readonly<Record<MemberRole, string>> = {
  owner: 'today',
  admin: 'today',
  teacher: 'my-teaching',
  examiner: 'to-grade',
};

export function presetFilters(key: string): WorkFilters {
  const preset = PRESETS.find((candidate) => candidate.key === key);
  return { ...NO_FILTERS, ...(preset?.filters ?? {}) };
}

function sameSet<T>(a: ReadonlySet<T>, b: ReadonlySet<T>): boolean {
  return a.size === b.size && [...a].every((value) => b.has(value));
}

export function sameFilters(a: WorkFilters, b: WorkFilters): boolean {
  return (
    sameSet(a.kinds, b.kinds) &&
    sameSet(a.skills, b.skills) &&
    a.state === b.state &&
    a.assignee === b.assignee &&
    a.boardId === b.boardId &&
    a.batchId === b.batchId &&
    a.from === b.from &&
    a.to === b.to &&
    a.search === b.search &&
    a.undated === b.undated &&
    a.sort === b.sort
  );
}

/** Which preset tab should read as current, or null once the user has edited
 *  away from all of them. */
export function matchingPreset(filters: WorkFilters): string | null {
  return PRESETS.find((preset) => sameFilters(filters, presetFilters(preset.key)))?.key ?? null;
}

/* ---------------------------------------------------------- active chips -- */

export interface ActiveFilterChip {
  key: string;
  label: string;
  /** The patch that removes just this one facet. */
  clear: Partial<WorkFilters>;
}

export interface ChipLookups {
  boards: readonly Lookup[];
  batches: readonly Lookup[];
  staff: readonly { user_id: string; display_name: string }[];
}

function nameOf(list: readonly Lookup[], id: string, fallback: string): string {
  return list.find((entry) => entry.id === id)?.name ?? fallback;
}

/**
 * One chip per facet the user has actually set, each carrying the patch that
 * clears it. The point is that nothing filters invisibly: if the list is short,
 * the reason is on screen and one click from being undone.
 *
 * `kinds` is deliberately absent — the kind chips are already visible above,
 * and echoing them here would say the same thing twice.
 */
export function activeFilterChips(filters: WorkFilters, lookups: ChipLookups): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];

  if (filters.assignee !== 'any') {
    const label =
      filters.assignee === 'me'
        ? 'Mine'
        : filters.assignee === 'unassigned'
          ? 'Nobody has it'
          : (lookups.staff.find((member) => member.user_id === filters.assignee)?.display_name ??
            'Someone');
    chips.push({ key: 'assignee', label, clear: { assignee: 'any' } });
  }

  if (filters.state !== 'any') {
    const label =
      STATE_OPTIONS.find((option) => option.key === filters.state)?.label ?? filters.state;
    chips.push({ key: 'state', label, clear: { state: 'any' } });
  }

  if (filters.boardId !== null) {
    chips.push({
      key: 'board',
      label: `Board: ${nameOf(lookups.boards, filters.boardId, 'Board')}`,
      clear: { boardId: null },
    });
  }

  if (filters.batchId !== null) {
    chips.push({
      key: 'batch',
      label: `Batch: ${nameOf(lookups.batches, filters.batchId, 'Batch')}`,
      clear: { batchId: null },
    });
  }

  for (const skill of WORK_SKILLS) {
    if (!filters.skills.has(skill)) continue;
    const next = new Set(filters.skills);
    next.delete(skill);
    chips.push({
      key: `skill-${skill}`,
      // eslint-disable-next-line security/detect-object-injection -- `skill` is the WorkSkill literal union from WORK_SKILLS and the record is complete over it
      label: SKILL_LABELS[skill],
      clear: { skills: next },
    });
  }

  if (filters.from !== null) {
    chips.push({
      key: 'from',
      label: `From ${dayjs(filters.from).format('D MMM')}`,
      clear: { from: null },
    });
  }
  if (filters.to !== null) {
    chips.push({
      key: 'to',
      label: `Until ${dayjs(filters.to).format('D MMM')}`,
      clear: { to: null },
    });
  }

  if (filters.search.trim() !== '') {
    chips.push({ key: 'search', label: `“${filters.search.trim()}”`, clear: { search: '' } });
  }

  if (filters.undated) {
    chips.push({ key: 'undated', label: 'Including no date', clear: { undated: false } });
  }

  return chips;
}

/* ------------------------------------------------------------- URL codec -- */

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isKind(value: string): value is WorkKind {
  return (WORK_KINDS as readonly string[]).includes(value);
}
function isSkill(value: string): value is WorkSkill {
  return (WORK_SKILLS as readonly string[]).includes(value);
}
function isState(value: string): value is StateFilter {
  return STATE_OPTIONS.some((option) => option.key === value);
}
function isSort(value: string): value is SortBy {
  return SORT_OPTIONS.some((option) => option.key === value);
}

/**
 * Serialise to a query string.
 *
 * A facet at its default deletes its param, so a shared link carries only what
 * the sender actually chose and `/institute/work` stays readable. Same
 * convention as the invoice list, which documents why URL filters exist here at
 * all: a filter a link cannot set is a filter every callout has to work around.
 */
export function encodeFilters(filters: WorkFilters, preset: string | null): URLSearchParams {
  const params = new URLSearchParams();
  if (preset !== null) params.set('view', preset);
  if (filters.kinds.size > 0) {
    params.set('kinds', WORK_KINDS.filter((kind) => filters.kinds.has(kind)).join(','));
  }
  if (filters.state !== 'any') params.set('state', filters.state);
  if (filters.assignee !== 'any') params.set('who', filters.assignee);
  if (filters.boardId !== null) params.set('board', filters.boardId);
  if (filters.batchId !== null) params.set('batch', filters.batchId);
  if (filters.skills.size > 0) {
    params.set('skills', WORK_SKILLS.filter((skill) => filters.skills.has(skill)).join(','));
  }
  if (filters.from !== null) params.set('from', filters.from);
  if (filters.to !== null) params.set('to', filters.to);
  if (filters.search.trim() !== '') params.set('q', filters.search.trim());
  if (filters.undated) params.set('undated', '1');
  if (filters.sort !== 'due') params.set('sort', filters.sort);
  return params;
}

/**
 * Read a query string back.
 *
 * A `view=` names the preset the explicit params are layered onto, so
 * `?view=to-grade&who=me` means "the To grade view, mine only" and the tab
 * stays lit. Unknown or malformed values are dropped rather than rejected — a
 * hand-edited URL should degrade to a wider list, never to an error page.
 */
export function decodeFilters(params: URLSearchParams): {
  filters: WorkFilters;
  preset: string | null;
} {
  const view = params.get('view');
  const preset = view !== null && PRESETS.some((p) => p.key === view) ? view : null;
  const base = preset === null ? NO_FILTERS : presetFilters(preset);
  const filters: WorkFilters = { ...base };

  const kinds = params.get('kinds');
  if (kinds !== null) filters.kinds = new Set(kinds.split(',').filter(isKind));

  const skills = params.get('skills');
  if (skills !== null) filters.skills = new Set(skills.split(',').filter(isSkill));

  const state = params.get('state');
  if (state !== null && isState(state)) filters.state = state;

  const who = params.get('who');
  if (who !== null && who !== '') filters.assignee = who;

  filters.boardId = params.get('board');
  filters.batchId = params.get('batch');

  const from = params.get('from');
  filters.from = from !== null && DATE_RE.test(from) ? from : null;
  const to = params.get('to');
  filters.to = to !== null && DATE_RE.test(to) ? to : null;

  filters.search = params.get('q') ?? '';
  filters.undated = params.get('undated') === '1';

  const sort = params.get('sort');
  if (sort !== null && isSort(sort)) filters.sort = sort;

  return { filters, preset };
}

/**
 * Whether the server should narrow to the caller's own work.
 *
 * The RPC's `p_mine` means "mine OR unassigned", so it is exactly right for
 * those two assignee choices and wrong for every other one — asking for a named
 * colleague's rows with `p_mine` on would return nobody's. Keeping this one
 * function means the query key has two variants rather than one per colleague.
 */
export function wantsMine(filters: WorkFilters): boolean {
  return filters.assignee === 'me' || filters.assignee === 'unassigned';
}
